import { defineStore } from "pinia";
import { useSnackBarStore } from "@/shared/stores/snackbar";
import axios from "@/app/plugins/axios";
import {
  TerminalForm,
  TerminalFormInput,
  TerminalFormStore,
} from "@/terminal-forms/types/terminalForm";
import { getErrorMessage } from "@/shared/utils/errorMessage";

interface TerminalFormListResponse {
  data: TerminalForm[];
  previousPage: string | null;
  nextPage: string | null;
  hasMore: boolean;
}

export interface CreateTerminalFormPayload {
  name: string;
  description?: string;
  inputs: TerminalFormInput[];
}

export interface UpdateTerminalFormPayload {
  name?: string;
  description?: string;
  archived?: boolean;
  type?: string;
}

export const useTerminalFormsStore = defineStore("terminalForms", {
  state: (): TerminalFormStore => ({
    loading: false,
    startAfter: null,
    endBefore: null,
    name: null,
    archived: null,
    forms: [],
    previousPage: null,
    nextPage: null,
    hasMore: false,
    terminalFormPayload: [],
    selectedForm: null,
  }),
  actions: {
    setStartAfter(startAfter: string | null) {
      this.startAfter = startAfter;
    },
    setName(name: string | null) {
      this.name = name;
    },
    setEndBefore(endBefore: string | null) {
      this.endBefore = endBefore;
    },
    setArchived(archived: boolean | null) {
      this.archived = archived;
    },
    setTerminalFormPayload(payload: TerminalFormInput) {
      this.terminalFormPayload = [...this.terminalFormPayload, payload];
    },
    removeTerminalFormPayload(payload: TerminalFormInput) {
      this.terminalFormPayload = this.terminalFormPayload.filter((item) => item !== payload);
    },
    clearTerminalFormPayload() {
      this.terminalFormPayload = [];
    },
    clearSelectedForm() {
      this.selectedForm = null;
    },
    async listTerminalForms(): Promise<TerminalForm[] | undefined> {
      this.loading = true;
      try {
        let query = "/terminalForm";
        const queryParams: string[] = [];

        if (this.startAfter) {
          queryParams.push(`startAfter=${this.startAfter}`);
        }
        if (this.endBefore) {
          queryParams.push(`endBefore=${this.endBefore}`);
        }
        if (this.name) {
          queryParams.push(`name=${this.name}`);
        }
        if (this.archived) {
          queryParams.push(`archived=${this.archived}`);
        }

        if (queryParams.length > 0) {
          query += `?${queryParams.join("&")}`;
        }

        const res = await axios.get<TerminalFormListResponse>(query);
        this.forms = res.data.data;
        this.previousPage = res.data.previousPage;
        this.hasMore = res.data.hasMore;
        this.nextPage = res.data.nextPage;
        // Reset navigation markers if new data is fetched
        if (this.startAfter) {
          this.endBefore = null; // Reset endBefore if fetching next page
        } else if (this.endBefore) {
          this.startAfter = null; // Reset startAfter if fetching previous page
        }
        return res.data.data;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
        return undefined;
      } finally {
        this.loading = false;
      }
    },

    async updateTerminalFormStatus(status: boolean, id: string): Promise<TerminalForm | undefined> {
      this.loading = true;
      try {
        const res = await axios.patch<TerminalForm>(`/terminalForm/${id}`, {
          archived: status,
        });
        return res.data;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
        return undefined;
      } finally {
        this.loading = false;
      }
    },

    async storeTerminalForm(payload: CreateTerminalFormPayload): Promise<TerminalForm | undefined> {
      this.loading = true;
      try {
        const res = await axios.post<TerminalForm>("/terminalForm", payload);
        return res.data;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
        return undefined;
      } finally {
        this.loading = false;
      }
    },
    async fetchTerminalFormDetail(id: string): Promise<TerminalForm | undefined> {
      this.loading = true;
      try {
        const res = await axios.get<TerminalForm>(`/terminalForm/${id}`);
        this.selectedForm = res.data;
        return res.data;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
        throw error;
      } finally {
        this.loading = false;
      }
    },
    async retrieveTerminalForm(id: string): Promise<TerminalForm | null> {
      try {
        const form = await this.fetchTerminalFormDetail(id);
        if (!form) {
          return null;
        }
        this.terminalFormPayload = form.inputs;
        return form;
      } catch {
        return null;
      }
    },
    async updateTerminalForm(
      data: UpdateTerminalFormPayload,
      id: string,
    ): Promise<TerminalForm | undefined> {
      this.loading = true;
      try {
        const res = await axios.patch<TerminalForm>(`/terminalForm/${id}`, data);
        return res.data;
      } catch (error) {
        useSnackBarStore().toastError({ message: getErrorMessage(error) });
        return undefined;
      } finally {
        this.loading = false;
      }
    },
    async sendTerminalForm(terminalId: string, formId: string, data: unknown): Promise<unknown> {
      this.loading = true;
      try {
        const res = await axios.post(`/terminal/${terminalId}/collectInputs/${formId}`, data);
        return res.data;
      } finally {
        this.loading = false;
      }
    },
  },
});
